import { useCallback, useEffect, useRef, useState } from "react";

function storageKey(role, userId) {
  return `dh_j_tour_${role || "agent"}_${userId || "guest"}`;
}

function readProgress(role, userId) {
  try {
    return JSON.parse(localStorage.getItem(storageKey(role, userId)) || "null");
  } catch {
    return null;
  }
}

function writeProgress(role, userId, payload) {
  localStorage.setItem(storageKey(role, userId), JSON.stringify(payload));
}

export function useTourPlayer(sections, { role, userId }) {
  const saved = readProgress(role, userId);
  const [index, setIndex] = useState(saved?.index || 0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(saved?.volume ?? 0.85);
  const [completed, setCompleted] = useState(Boolean(saved?.completed));
  const [furthest, setFurthest] = useState(saved?.furthest || 0);
  const indexRef = useRef(index);
  indexRef.current = index;

  const persist = useCallback(
    (patch) => {
      const next = {
        index: indexRef.current,
        furthest,
        completed,
        volume,
        updated_at: new Date().toISOString(),
        ...patch,
      };
      writeProgress(role, userId, next);
      return next;
    },
    [completed, furthest, role, userId, volume]
  );

  const stopSpeech = useCallback(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
  }, []);

  const speak = useCallback(
    (text, onEnd) => {
      stopSpeech();
      if (muted || typeof window === "undefined" || !window.speechSynthesis) {
        return;
      }
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.volume = volume;
      utterance.rate = 0.95;
      utterance.onend = () => onEnd?.();
      window.speechSynthesis.speak(utterance);
    },
    [muted, stopSpeech, volume]
  );

  const finishIfLast = useCallback(
    (fromIndex) => {
      if (fromIndex < sections.length - 1) return false;
      setCompleted(true);
      persist({ completed: true, index: fromIndex, furthest: sections.length - 1 });
      return true;
    },
    [persist, sections.length]
  );

  const playFrom = useCallback(
    (nextIndex) => {
      const section = sections[nextIndex];
      if (!section) return;
      setIndex(nextIndex);
      setPlaying(true);
      setFurthest((current) => Math.max(current, nextIndex));
      persist({ index: nextIndex, furthest: Math.max(furthest, nextIndex) });
      speak(section.script, () => {
        if (nextIndex >= sections.length - 1) {
          setPlaying(false);
          finishIfLast(nextIndex);
          return;
        }
        playFrom(nextIndex + 1);
      });
    },
    [finishIfLast, furthest, persist, sections, speak]
  );

  function play() {
    playFrom(index);
  }

  function pause() {
    setPlaying(false);
    if (typeof window !== "undefined" && window.speechSynthesis?.speaking) {
      window.speechSynthesis.pause();
    } else {
      stopSpeech();
    }
  }

  function resumeOrPlay() {
    if (typeof window !== "undefined" && window.speechSynthesis?.paused) {
      window.speechSynthesis.resume();
      setPlaying(true);
      return;
    }
    play();
  }

  function jump(nextIndex) {
    if (!completed && nextIndex > furthest) return;
    stopSpeech();
    setPlaying(false);
    setIndex(nextIndex);
    persist({ index: nextIndex });
  }

  function nextSection() {
    stopSpeech();
    setPlaying(false);
    const atEnd = index >= sections.length - 1;
    if (atEnd) {
      finishIfLast(index);
      return;
    }
    const nextIndex = index + 1;
    setFurthest((current) => Math.max(current, nextIndex));
    setIndex(nextIndex);
    persist({ index: nextIndex, furthest: Math.max(furthest, nextIndex) });
  }

  useEffect(() => () => stopSpeech(), [stopSpeech]);

  return {
    index,
    playing,
    muted,
    volume,
    completed,
    furthest,
    section: sections[index],
    canJump: (target) => completed || target <= furthest,
    play: resumeOrPlay,
    pause,
    jump,
    nextSection,
    setMuted: (value) => {
      setMuted(value);
      if (value) stopSpeech();
    },
    setVolume: (value) => {
      setVolume(value);
      persist({ volume: value });
    },
  };
}
